import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { fdiDetailData } from '../../../data/fdiData';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
  PieChart,
  Pie,
} from 'recharts';
import { getTooltipStyle } from '../../../hooks/useExpandedStyles';
import { Building2, Globe, TrendingUp, Users } from 'lucide-react';

const getTypeBadgeStyle = (type: string): string => {
  const styles: Record<string, string> = {
    'SOE': 'bg-data-amber/20 text-data-amber',
    'PUBLIC': 'bg-data-blue/20 text-data-blue',
    'PRIVATE': 'bg-gray-600/30 text-gray-400',
  };
  return styles[type] || 'bg-gray-600/30 text-gray-400';
};

const getStatusBadgeStyle = (status: string): string => {
  const styles: Record<string, string> = {
    'ACTIVE': 'bg-data-green/20 text-data-green',
    'NEW': 'bg-data-green/30 text-data-green',
    'EXPANSION': 'bg-cyan-500/20 text-cyan-400',
  };
  return styles[status] || 'bg-gray-600/30 text-gray-400';
};

const COLORS = ['#00d26a', '#00a3ff', '#ffb800', '#a855f7', '#ec4899', '#06b6d4', '#30363d'];

interface TopInvestorsPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
  isExpanded?: boolean;
}

export const TopInvestorsPanel: React.FC<TopInvestorsPanelProps> = ({
  isMinimized,
  onMinimize,
  isExpanded = false,
}) => {
  const { topInvestors, keyMetrics } = fdiDetailData;

  // Calculate aggregated data for charts
  const totalInvested = topInvestors.reduce((sum, inv) => sum + inv.invested, 0);
  const byCountry = topInvestors.reduce((acc, inv) => {
    acc[inv.country] = (acc[inv.country] || 0) + inv.invested;
    return acc;
  }, {} as Record<string, number>);

  const bySector = topInvestors.reduce((acc, inv) => {
    acc[inv.sector] = (acc[inv.sector] || 0) + inv.invested;
    return acc;
  }, {} as Record<string, number>);

  const countryData = Object.entries(byCountry)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const sectorData = Object.entries(bySector)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const expandedContent = (
    <div className="flex flex-col h-full gap-4 md:gap-8">
      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="flex items-center gap-1 md:gap-2 text-gray-500 text-xs md:text-sm uppercase mb-1 md:mb-2">
            <Building2 size={12} className="md:w-[14px] md:h-[14px]" />
            <span>Total Investors</span>
          </div>
          <div className="text-xl md:text-3xl font-bold text-white">{topInvestors.length}</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Major companies</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="flex items-center gap-1 md:gap-2 text-gray-500 text-xs md:text-sm uppercase mb-1 md:mb-2">
            <TrendingUp size={12} className="md:w-[14px] md:h-[14px]" />
            <span>Total Invested</span>
          </div>
          <div className="text-xl md:text-3xl font-bold text-data-green">${totalInvested.toFixed(1)}B</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Combined investment</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="flex items-center gap-1 md:gap-2 text-gray-500 text-xs md:text-sm uppercase mb-1 md:mb-2">
            <Globe size={12} className="md:w-[14px] md:h-[14px]" />
            <span>Active Projects</span>
          </div>
          <div className="text-xl md:text-3xl font-bold text-data-amber">{keyMetrics.totalProjects}</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Ongoing operations</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="flex items-center gap-1 md:gap-2 text-gray-500 text-xs md:text-sm uppercase mb-1 md:mb-2">
            <Users size={12} className="md:w-[14px] md:h-[14px]" />
            <span>New in 2024</span>
          </div>
          <div className="text-xl md:text-3xl font-bold text-data-green">{keyMetrics.newProjects2024}</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">New projects</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Investment by Country */}
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
            Investment by Country (USD Billions)
          </h3>
          <div className="h-[200px] md:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={countryData}
                layout="vertical"
                margin={{ top: 10, right: 20, bottom: 10, left: 50 }}
              >
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  tickFormatter={(v) => `$${v}B`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  width={45}
                />
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number) => [`$${value.toFixed(2)}B`, 'Investment']}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {countryData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Investment by Sector */}
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
            Investment by Sector
          </h3>
          <div className="h-[200px] md:h-[300px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sectorData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="30%"
                  outerRadius="55%"
                  paddingAngle={2}
                >
                  {sectorData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number) => [`$${value.toFixed(2)}B`, 'Investment']}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-xs md:text-sm text-gray-500">TOTAL</div>
                <div className="text-lg md:text-2xl font-bold text-white">${totalInvested.toFixed(1)}B</div>
              </div>
            </div>
          </div>
          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-4 mt-3 md:mt-4">
            {sectorData.slice(0, 5).map((entry, i) => (
              <div key={entry.name} className="flex items-center gap-1 md:gap-2">
                <span className="w-2 h-2 md:w-3 md:h-3 rounded" style={{ backgroundColor: COLORS[i] }} />
                <span className="text-xs md:text-sm text-gray-400">{entry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-terminal-bg rounded-lg p-3 md:p-6">
        <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
          Top Investors Detail
        </h3>
        <div className="overflow-auto -mx-3 px-3 md:mx-0 md:px-0">
          <table className="w-full text-xs md:text-sm min-w-[600px]">
            <thead>
              <tr className="text-gray-500 uppercase text-xs md:text-sm tracking-wide border-b border-terminal-border">
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium">Company</th>
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium hidden sm:table-cell">Country</th>
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium hidden sm:table-cell">Sector</th>
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium">Type</th>
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium hidden md:table-cell">Status</th>
                <th className="text-right py-2 md:py-3 px-2 md:px-4 font-medium">Investment</th>
                <th className="text-left py-2 md:py-3 px-2 md:px-4 font-medium hidden lg:table-cell">Share</th>
              </tr>
            </thead>
            <tbody>
              {topInvestors.map((inv, index) => (
                <tr
                  key={inv.company}
                  className="border-b border-terminal-border/30 hover:bg-terminal-panel/50 transition-colors"
                >
                  <td className="py-2 md:py-3 px-2 md:px-4 text-white font-semibold">{inv.company}</td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-gray-400 hidden sm:table-cell">{inv.country}</td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-gray-400 hidden sm:table-cell">{inv.sector}</td>
                  <td className="py-2 md:py-3 px-2 md:px-4">
                    <span className={`px-1.5 md:px-2 py-0.5 md:py-1 rounded text-xs md:text-sm ${getTypeBadgeStyle(inv.type)}`}>
                      {inv.type}
                    </span>
                  </td>
                  <td className="py-2 md:py-3 px-2 md:px-4 hidden md:table-cell">
                    <span className={`px-1.5 md:px-2 py-0.5 md:py-1 rounded text-xs md:text-sm ${getStatusBadgeStyle(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="py-2 md:py-3 px-2 md:px-4 text-right text-data-green font-semibold">
                    ${inv.invested}B
                  </td>
                  <td className="py-2 md:py-3 px-2 md:px-4 hidden lg:table-cell">
                    <div className="w-16 md:w-24 h-1.5 md:h-2 bg-terminal-border rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(inv.invested / totalInvested) * 100}%`,
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Investor Insights */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-4">
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4 border-data-amber">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">SOE Investors</div>
          <div className="text-xl md:text-2xl font-bold text-white">
            {topInvestors.filter((i) => i.type === 'SOE').length}
          </div>
          <div className="text-xs md:text-sm text-gray-400 mt-0.5 md:mt-1">State-owned enterprises</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4 border-data-blue">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Public Companies</div>
          <div className="text-xl md:text-2xl font-bold text-white">
            {topInvestors.filter((i) => i.type === 'PUBLIC').length}
          </div>
          <div className="text-xs md:text-sm text-gray-400 mt-0.5 md:mt-1">Listed corporations</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4 border-data-green">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">New Entrants</div>
          <div className="text-xl md:text-2xl font-bold text-white">
            {topInvestors.filter((i) => i.status === 'NEW').length}
          </div>
          <div className="text-xs md:text-sm text-gray-400 mt-0.5 md:mt-1">First-time investors 2024</div>
        </div>
      </div>
    </div>
  );

  const compactContent = (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto custom-scrollbar">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-terminal-panel">
            <tr className="text-gray-500">
              <th className="text-left py-1 font-medium">COMPANY</th>
              <th className="text-left py-1 font-medium">CTRY</th>
              <th className="text-left py-1 font-medium">SECTOR</th>
              <th className="text-right py-1 font-medium">INVESTED</th>
            </tr>
          </thead>
          <tbody>
            {topInvestors.map((inv) => (
              <tr key={inv.company} className="border-t border-terminal-border/30">
                <td className="py-1.5">
                  <div className="text-white font-medium">{inv.company}</div>
                  <div className="flex gap-1 mt-0.5">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${getTypeBadgeStyle(inv.type)}`}>
                      {inv.type}
                    </span>
                    {inv.status !== 'ACTIVE' && (
                      <span className={`px-1.5 py-0.5 rounded text-xs ${getStatusBadgeStyle(inv.status)}`}>
                        {inv.status}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-1.5 text-gray-400">{inv.country}</td>
                <td className="py-1.5 text-gray-400">{inv.sector}</td>
                <td className="py-1.5 text-right text-data-green font-medium">${inv.invested}B</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );

  if (isExpanded) {
    return expandedContent;
  }

  return (
    <Panel
      title="TOP INVESTORS"
      subtitle="MAJOR PLAYERS"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      expandedContent={expandedContent}
    >
      {compactContent}
    </Panel>
  );
};

export default TopInvestorsPanel;
